const columnIndex = (cellRef: string) => {
  const letters = cellRef.replace(/\d/g, "").toUpperCase();
  let index = 0;
  for (let i = 0; i < letters.length; i++) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index - 1;
};

const columnLetter = (index: number) => {
  let number = index + 1;
  let letters = "";
  while (number > 0) {
    const remainder = (number - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    number = Math.floor((number - 1) / 26);
  }
  return letters;
};

const escapeXml = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const findZipEnd = (bytes: Uint8Array) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const start = Math.max(0, bytes.length - 22 - 65535);
  for (let index = bytes.length - 22; index >= start; index--) {
    if (view.getUint32(index, true) === 0x06054b50) {
      return index;
    }
  }
  return -1;
};

const unzipSheetXml = async (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const endOffset = findZipEnd(bytes);
  if (endOffset < 0) {
    throw new Error("Unable to read the Excel sheet");
  }
  const fileCount = view.getUint16(endOffset + 10, true);
  let cursor = view.getUint32(endOffset + 16, true);
  const files: Record<string, Uint8Array> = {};
  for (let index = 0; index < fileCount; index++) {
    if (view.getUint32(cursor, true) !== 0x02014b50) {
      break;
    }
    const method = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const name = new TextDecoder().decode(bytes.slice(cursor + 46, cursor + 46 + nameLength)).replace(/\\/g, "/");
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = bytes.slice(dataStart, dataStart + compressedSize);
    if (method === 0) {
      files[name] = compressed;
    } else if (method === 8) {
      const inflated = new Blob([compressed]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
      files[name] = new Uint8Array(await new Response(inflated).arrayBuffer());
    }
    cursor += 46 + nameLength + extraLength + commentLength;
  }
  const decoder = new TextDecoder();
  return Object.fromEntries(
    Object.entries(files)
      .filter(([name]) => name.toLowerCase().endsWith(".xml"))
      .map(([name, data]) => [name, decoder.decode(data)])
  );
};

const decodeXml = (value: string) =>
  value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");

const xmlTag = (name: string) => `(?:[\\w]+:)?${name}`;

const builtInNumFormats: Record<number, string> = {
  1: "0",
  2: "0.00",
  3: "#,##0",
  4: "#,##0.00",
  7: "$#,##0.00",
  8: "$#,##0.00",
  9: "0%",
  10: "0.00%",
  11: "0.00E+00",
  37: "#,##0",
  38: "#,##0",
  39: "#,##0.00",
  40: "#,##0.00",
  41: "#,##0",
  42: "#,##0",
  43: "#,##0.00",
  44: "#,##0.00",
  48: "##0.0E+0",
  49: "@",
};

const readCellFormats = (stylesXml: string) => {
  const custom: Record<number, string> = {};
  Array.from(stylesXml.matchAll(new RegExp(`<${xmlTag("numFmt")}\\b([^>]*)\\/?>`, "g"))).forEach((item) => {
    const id = item[1].match(/numFmtId="(\d+)"/)?.[1];
    const code = item[1].match(/formatCode="([^"]*)"/)?.[1];
    if (id != null && code != null) {
      custom[Number(id)] = decodeXml(code);
    }
  });
  const cellXfs = stylesXml.match(new RegExp(`<${xmlTag("cellXfs")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("cellXfs")}>`));
  if (cellXfs == null) {
    return [];
  }
  return Array.from(cellXfs[1].matchAll(new RegExp(`<${xmlTag("xf")}\\b([^>]*)\\/?>`, "g"))).map((item) => {
    const id = Number(item[1].match(/numFmtId="(\d+)"/)?.[1] ?? "0");
    return custom[id] ?? builtInNumFormats[id] ?? "General";
  });
};

const decimalPlaces = (format: string) => {
  const section = format
    .split(";")[0]
    .replace(/\[[^\]]*\]/g, "")
    .replace(/"[^"]*"/g, "");
  if (section === "" || section === "General" || section === "@") {
    return null;
  }
  const decimal = section.match(/\.([0#?]+)/);
  if (decimal == null) {
    return /[0#?]/.test(section) ? { min: 0, max: 0 } : null;
  }
  const token = decimal[1];
  return { min: (token.match(/0/g) ?? []).length, max: token.length };
};

const applyNumberFormat = (raw: string, format: string) => {
  const places = decimalPlaces(format);
  const value = Number(raw);
  if (places == null || !Number.isFinite(value)) {
    return raw;
  }
  const text = value.toFixed(places.max);
  if (places.min === places.max) {
    return text;
  }
  const [whole, fraction] = text.split(".");
  if (fraction == null) {
    return whole;
  }
  let end = fraction.length;
  while (end > places.min && fraction[end - 1] === "0") {
    end -= 1;
  }
  return end === 0 ? whole : `${whole}.${fraction.slice(0, end)}`;
};

const readSharedStrings = (xml: string) =>
  Array.from(xml.matchAll(new RegExp(`<${xmlTag("si")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("si")}>`, "g"))).map((item) =>
    Array.from(item[1].matchAll(new RegExp(`<${xmlTag("t")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("t")}>`, "g")))
      .map((text) => decodeXml(text[1]))
      .join("")
  );

const parseSheetRows = (xml: string, sharedStrings: string[], formats: string[]) =>
  Array.from(xml.matchAll(new RegExp(`<${xmlTag("row")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("row")}>`, "g"))).map((row) => {
    const values: string[] = [];
    let nextIndex = 0;
    Array.from(row[1].matchAll(new RegExp(`<${xmlTag("c")}\\b([^>]*?)(?:/>|>([\\s\\S]*?)<\\/${xmlTag("c")}>)`, "g"))).forEach((cell) => {
      const attributes = cell[1];
      const body = cell[2] ?? "";
      const type = attributes.match(/\bt="([^"]*)"/)?.[1];
      const reference = attributes.match(/\br="([^"]*)"/)?.[1] ?? "";
      const styleIndex = attributes.match(/\bs="(\d+)"/)?.[1];
      const rawValue = decodeXml(body.match(new RegExp(`<${xmlTag("v")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("v")}>`))?.[1] ?? "");
      const inlineText = Array.from(body.matchAll(new RegExp(`<${xmlTag("t")}\\b[^>]*>([\\s\\S]*?)<\\/${xmlTag("t")}>`, "g")))
        .map((text) => decodeXml(text[1]))
        .join("");
      let text = rawValue;
      if (type === "s") {
        text = sharedStrings[Number(rawValue)] ?? "";
      } else if (type === "inlineStr" || inlineText !== "") {
        text = inlineText || rawValue;
      } else if (/^-?\d+(\.\d+)?(?:[eE][+-]?\d+)?$/.test(text)) {
        const format = styleIndex == null ? "General" : formats[Number(styleIndex)] ?? "General";
        text = applyNumberFormat(text, format);
      }
      const index = reference === "" ? nextIndex : columnIndex(reference);
      values[index] = text.trim();
      nextIndex = index + 1;
    });
    return values;
  });

export const readUploadRows = async (file: File) => {
  if (file.name.toLowerCase().endsWith(".csv")) {
    const text = (await file.text()).replace(/^\uFEFF/, "");
    return text
      .split(/\r?\n/)
      .filter((line) => line.trim() !== "")
      .map((line) => line.split(",").map((value) => value.trim().replace(/^"|"$/g, "")));
  }
  const xmlFiles = await unzipSheetXml(await file.arrayBuffer());
  const sharedName = Object.keys(xmlFiles).find((name) => name.toLowerCase().endsWith("sharedstrings.xml"));
  const sharedStrings = readSharedStrings(sharedName == null ? "" : xmlFiles[sharedName]);
  const stylesName = Object.keys(xmlFiles).find((name) => name.toLowerCase().endsWith("styles.xml"));
  const formats = readCellFormats(stylesName == null ? "" : xmlFiles[stylesName]);
  const sheetName = Object.keys(xmlFiles).find((name) => /worksheets\/sheet\d+\.xml$/i.test(name));
  if (sheetName == null) {
    throw new Error("Unable to read the Excel sheet");
  }
  return parseSheetRows(xmlFiles[sheetName], sharedStrings, formats);
};

const crc32 = (data: Uint8Array) => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i];
    for (let bit = 0; bit < 8; bit++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const zipStoredFiles = (entries: { name: string; data: Uint8Array }[]) => {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  entries.forEach((entry) => {
    const name = encoder.encode(entry.name);
    const header = new Uint8Array(30 + name.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(8, 0, true);
    view.setUint32(14, crc32(entry.data), true);
    view.setUint32(18, entry.data.length, true);
    view.setUint32(22, entry.data.length, true);
    view.setUint16(26, name.length, true);
    header.set(name, 30);
    locals.push(header, entry.data);
    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint32(16, crc32(entry.data), true);
    centralView.setUint32(20, entry.data.length, true);
    centralView.setUint32(24, entry.data.length, true);
    centralView.setUint16(28, name.length, true);
    centralView.setUint32(42, offset, true);
    central.set(name, 46);
    centrals.push(central);
    offset += header.length + entry.data.length;
  });
  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);
  const parts = [...locals, ...centrals, end];
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const zip = new Uint8Array(total);
  let cursor = 0;
  parts.forEach((part) => {
    zip.set(part, cursor);
    cursor += part.length;
  });
  return zip;
};

const worksheetXml = (rows: string[][]) => {
  const sheetRows = rows
    .map((row, rowIndex) => {
      const cells = row.map((value, index) => `<c r="${columnLetter(index)}${rowIndex + 1}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`).join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>
    ${sheetRows}
  </sheetData>
</worksheet>`;
};

export const downloadExcelSheet = (fileName: string, sheetName: string, rows: string[][]) => {
  const encoder = new TextEncoder();
  const sheet = worksheetXml(rows);
  const zip = zipStoredFiles([
    {
      name: "[Content_Types].xml",
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`),
    },
    {
      name: "_rels/.rels",
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      name: "xl/workbook.xml",
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`),
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`),
    },
    { name: "xl/worksheets/sheet1.xml", data: encoder.encode(sheet) },
  ]);
  const url = URL.createObjectURL(new Blob([zip], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadExcelTemplate = (fileName: string, sheetName: string, headers: string[]) => {
  downloadExcelSheet(fileName, sheetName, [headers]);
};

export const downloadCodeTypeTemplate = () => {
  downloadExcelTemplate("Code Type Template.xlsx", "Code Types", ["Code Type", "Code Type Percentage"]);
};
